import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { useEffect, useState } from 'react';
import {
  Area,
  CartesianGrid,
  Legend,
  AreaChart as RechartsAreaChart,
  XAxis,
  YAxis,
} from 'recharts';
import { DEFAULT_ANIMATION, DEFAULT_AXIS_CONFIG } from '../constants';
import { AreaChartProps } from '../types';
import {
  formatDate,
  formatValue,
  getChartColors,
  isMobileDevice,
  reduceDataPoints,
} from '../utils';
import { ChartEmpty, ChartError, ChartLoading } from './ChartStates';

export function AreaChart({
  data,
  dataKeys,
  format = 'number',
  currencyCode,
  stacked = false,
  colors,
  tooltip,
  xAxis,
  yAxis,
  legend,
  showLegend = false,
  animate = DEFAULT_ANIMATION.enabled,
  loading,
  error,
  emptyState,
  fillParent = false,
  className = '',
}: AreaChartProps) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(isMobileDevice());
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle loading state
  if (loading) {
    return <ChartLoading fillParent className={className} />;
  }

  // Handle error state
  if (error) {
    return <ChartError error={error} fillParent className={className} />;
  }

  // Handle empty state
  if (!data || data.length === 0) {
    return (
      <ChartEmpty fillParent className={className}>
        {emptyState}
      </ChartEmpty>
    );
  }

  // Reduce data points on mobile
  const chartData = isMobile ? reduceDataPoints(data) : data;

  // Determine which keys to display
  const keys = dataKeys || ['value'];

  // Get colors for areas
  const areaColors = colors || getChartColors(keys.length);

  // Build chart config
  const chartConfig: ChartConfig = keys.reduce((config, key, index) => {
    config[key] = {
      label: key.charAt(0).toUpperCase() + key.slice(1),
      color: areaColors[index],
    };
    return config;
  }, {} as ChartConfig);

  // Custom formatter for tooltip
  const tooltipFormatter =
    tooltip?.formatter ||
    ((value: number) => {
      return formatValue(value, format, currencyCode);
    });

  // Axis tick formatter
  const xAxisFormatter =
    xAxis?.tickFormatter ||
    ((value: string) => {
      return formatDate(value, 'short');
    });

  const yAxisFormatter =
    yAxis?.tickFormatter ||
    ((value: number) => {
      return formatValue(value, format, currencyCode);
    });

  // Map legend align to recharts format
  const legendAlign =
    legend?.align === 'start'
      ? 'left'
      : legend?.align === 'end'
        ? 'right'
        : 'center';

  const containerClass = fillParent
    ? `absolute inset-0 ${className}`
    : `w-full min-h-64 ${className}`;

  return (
    <div className={containerClass}>
      <ChartContainer config={chartConfig} className="h-full w-full">
        <RechartsAreaChart
          data={chartData}
          margin={{ top: 2, right: 0, bottom: 0, left: -16 }}
        >
          <defs>
            {keys.map((key, index) => (
              <linearGradient
                key={`gradient-${key}`}
                id={`gradient-${key}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor={areaColors[index]}
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor={areaColors[index]}
                  stopOpacity={0.1}
                />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="date"
            tickLine={
              xAxis?.show !== false ? DEFAULT_AXIS_CONFIG.tickLine : false
            }
            tickMargin={DEFAULT_AXIS_CONFIG.tickMargin}
            axisLine={
              xAxis?.show !== false ? DEFAULT_AXIS_CONFIG.axisLine : false
            }
            tickFormatter={xAxisFormatter}
            hide={xAxis?.show === false}
            label={
              xAxis?.label
                ? { value: xAxis.label, position: 'insideBottom', offset: -10 }
                : undefined
            }
          />
          <YAxis
            tickLine={
              yAxis?.show !== false ? DEFAULT_AXIS_CONFIG.tickLine : false
            }
            axisLine={
              yAxis?.show !== false ? DEFAULT_AXIS_CONFIG.axisLine : false
            }
            tickMargin={DEFAULT_AXIS_CONFIG.tickMargin}
            tickFormatter={yAxisFormatter}
            hide={yAxis?.show === false}
            domain={yAxis?.domain}
            label={
              yAxis?.label
                ? { value: yAxis.label, angle: -90, position: 'insideLeft' }
                : undefined
            }
          />
          {tooltip?.enabled !== false && (
            <ChartTooltip
              cursor={false}
              content={
                typeof tooltip?.content === 'function' ? (
                  tooltip.content
                ) : tooltip?.content ? (
                  () => tooltip.content as any
                ) : (
                  <ChartTooltipContent formatter={tooltipFormatter as any} />
                )
              }
            />
          )}
          {showLegend && legend?.position && (
            <Legend
              verticalAlign={legend.verticalAlign || 'bottom'}
              align={legendAlign as any}
            />
          )}
          {keys.map((key, index) => (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              stroke={areaColors[index]}
              strokeWidth={2}
              fill={`url(#gradient-${key})`}
              stackId={stacked ? 'stack' : undefined}
              isAnimationActive={animate}
              animationDuration={animate ? DEFAULT_ANIMATION.duration : 0}
            />
          ))}
        </RechartsAreaChart>
      </ChartContainer>
    </div>
  );
}
